import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UUID } from "crypto";
import api from "@/app/api";
import { reportKeys } from "../keys/reports";
import type {
  StockValueFilters,
  ReturnAnalysisFilters,
  BatchQuantityFilters,
  InventoryTurnoverFilters,
} from "@/app/api/reports/client";

// Stock Value Reports
export function useStockValueReport(filters?: StockValueFilters) {
  return useQuery({
    queryKey: reportKeys.stockValueWithFilters(filters),
    queryFn: () => api.reports.getStockValueReport(filters),
  });
}

// Stock Quantity (real/stored)
export function useStockQuantity(stockId: UUID | null) {
  return useQuery({
    queryKey: reportKeys.stockQuantity(stockId!),
    queryFn: () => api.reports.getStockQuantity(stockId!),
    enabled: !!stockId,
  });
}

// Stock Provisional Quantity
export function useStockProvisionalQuantity(stockId: UUID | null) {
  return useQuery({
    queryKey: reportKeys.stockProvisional(stockId!),
    queryFn: () => api.reports.getStockProvisionalQuantity(stockId!),
    enabled: !!stockId,
  });
}

// Stock at specific date
export function useStockAtDate(
  stockId: UUID | null,
  targetDate: string | null,
) {
  return useQuery({
    queryKey: reportKeys.stockAtDate(stockId!, targetDate!),
    queryFn: () => api.reports.getStockAtDate(stockId!, targetDate!),
    enabled: !!stockId && !!targetDate,
  });
}

// Stock Trajectory (full movement history)
export function useStockTrajectory(stockId: UUID | null) {
  return useQuery({
    queryKey: reportKeys.stockTrajectory(stockId!),
    queryFn: () => api.reports.getStockTrajectory(stockId!),
    enabled: !!stockId,
  });
}

// Stock Pending Movements
export function useStockPendingMovements(stockId: UUID | null) {
  return useQuery({
    queryKey: reportKeys.stockPending(stockId!),
    queryFn: () => api.reports.getStockPendingMovements(stockId!),
    enabled: !!stockId,
  });
}

// Stock Projection (daily flow details for chart)
export function useStockProjection(
  stockId: UUID | null,
  startDate: string | null,
  endDate: string | null,
) {
  return useQuery({
    queryKey: reportKeys.stockProjection(stockId!, startDate!, endDate!),
    queryFn: () =>
      api.stocks.getProjection(stockId!, {
        start_date: startDate!,
        end_date: endDate!,
      }),
    enabled: !!stockId && !!startDate && !!endDate,
  });
}

// Create Company Snapshots
export function useCreateCompanySnapshots() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.reports.createCompanySnapshots(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.snapshots() });
      queryClient.invalidateQueries({ queryKey: reportKeys.stockValue() });
    },
  });
}

// Create Stock Snapshot
export function useCreateStockSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stockId: UUID) => api.reports.createStockSnapshot(stockId),
    onSuccess: (_, stockId) => {
      queryClient.invalidateQueries({ queryKey: reportKeys.snapshots() });
      queryClient.invalidateQueries({
        queryKey: reportKeys.stockReports(stockId),
      });
    },
  });
}

// Flow Chain (trace purchase to sale)
export function useFlowChain(flowId: UUID | null) {
  return useQuery({
    queryKey: reportKeys.flowChain(flowId!),
    queryFn: () => api.reports.getFlowChain(flowId!),
    enabled: !!flowId,
  });
}

// Return Analysis (profitability report)
export function useReturnAnalysis(filters?: ReturnAnalysisFilters) {
  return useQuery({
    queryKey: reportKeys.returnAnalysisWithFilters(filters),
    queryFn: () => api.reports.getReturnAnalysis(filters),
  });
}

// Batch Stock Quantities (efficient multi-stock query)
export function useBatchStockQuantities(filters?: BatchQuantityFilters) {
  return useQuery({
    queryKey: reportKeys.batchQuantitiesWithFilters(filters),
    queryFn: () => api.reports.getBatchStockQuantities(filters),
  });
}

// Inventory Turnover Report
export function useInventoryTurnover(filters: InventoryTurnoverFilters | null) {
  return useQuery({
    queryKey: reportKeys.inventoryTurnoverWithFilters(filters!),
    queryFn: () => api.reports.getInventoryTurnover(filters!),
    enabled: !!filters?.start_date && !!filters?.end_date,
  });
}
